import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';

import App from '@/App';
import LmmView from '@/apps/lmm/LmmView';
import WtsView from '@/apps/materieelBeheer/wts/WtsView';
import MwdViewV2 from '@/apps/mwd-v2/MwdView';
import { ProtectedRoute } from '@/auth';
import { ErrorBoundary } from '@/router/errorBoundary';
import { envVariables } from '@/utils/env';
import PlankView from '@/views/ddp/PlankView';

const StatusView = lazy(() => import('@/apps/health/status/StatusView'));
const BeheerView = lazy(() => import('@/views/beheerView/BeheerView'));
const AgendaView = lazy(() => import('@/apps/agenda/AgendaView'));
const BRegelingenView = lazy(() => import('@/views/BRegelingenView/BRegelingenView'));
const DebugInfoView = lazy(() => import('@/views/debug/debugInfoView/DebugInfoView'));
const DebugWithNavBar = lazy(() => import('@/views/debug/debugInfoView/DebugWithNavBar'));
const EventsView = lazy(() => import('@/views/debug/sseView/EventsView'));

const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Laden...</div>
);

const redirectToAzure = ({ request }) => {
  const { featureRedirectToAzure, featureRedirectToAzureUrl } = envVariables();

  if (featureRedirectToAzure && featureRedirectToAzureUrl) {
    const subpath = request.url
      .replace(/^https?:\/\//g, '')
      .split('/')
      .slice(1)
      .join('/');
    throw redirect(String(`${featureRedirectToAzureUrl}${subpath}`));
  }
};

/**
 * React Router config.
 *
 * All routes use standard React Router rendering via <Outlet>.
 */
const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorBoundary />,
    element: <App />,
    middleware: [redirectToAzure],
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute anyOf={['platform.view', 'mwd.view']}>
            <MwdViewV2 />
          </ProtectedRoute>
        ),
      },
      {
        path: '/b-regelingen',
        element: (
          <ProtectedRoute requires='bregelingen.view'>
            <Suspense fallback={<LazyFallback />}>
              <BRegelingenView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/plank',
        element: (
          <ProtectedRoute requires='ddp.view'>
            <PlankView />
          </ProtectedRoute>
        ),
      },
      {
        path: '/lmm',
        element: (
          <ProtectedRoute check={({ hasPermission }) => hasPermission('lmm.view') && !!envVariables()?.featureLmm}>
            <LmmView />
          </ProtectedRoute>
        ),
      },
      {
        path: '/agenda',
        element: (
          <ProtectedRoute check={({ hasPermission }) => hasPermission('agenda.view') && !!envVariables().featureAgenda}>
            <Suspense fallback={<LazyFallback />}>
              <AgendaView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/status',
        element: (
          <ProtectedRoute anyOf={['platform.view', 'mwd.view']}>
            <Suspense fallback={<LazyFallback />}>
              <StatusView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/beheer',
        element: (
          <ProtectedRoute anyOf={['beheer.tam', 'beheer.matbeheer', 'lmm.view']}>
            <Suspense fallback={<LazyFallback />}>
              <BeheerView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/beheer/uitsluitingen',
        element: (
          <ProtectedRoute check={({ hasPermission }) => hasPermission('lmm.view') && !!envVariables().featureWts}>
            <WtsView />
          </ProtectedRoute>
        ),
      },
      {
        path: 'debug',
        element: (
          <Suspense fallback={<LazyFallback />}>
            <DebugWithNavBar />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to='env' replace />,
          },
          {
            path: 'env',
            element:
              envVariables().authProvider !== 'bff-cookie' ? (
                <Suspense fallback={<LazyFallback />}>
                  <DebugInfoView />
                </Suspense>
              ) : null,
          },
          {
            path: 'events',
            element: (
              <ProtectedRoute anyOf={['platform.view', 'mwd.view']}>
                <Suspense fallback={<LazyFallback />}>
                  <EventsView />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);

export default router;
